import { Sidebar } from 'primereact/sidebar';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Tag } from 'sharedUi/Tag';
import { Button } from 'sharedUi/Button';
import type { PromptExecutionRecentCall } from '../../types/prompt-execution.types';
import {
  getModelColors,
  getStatusSeverity,
  getAccuracyColor,
  formatCurrency,
  formatResponseTime,
} from '../../types/prompt-execution.types';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useQuery } from '@tanstack/react-query';
import { userLookupService } from '../../services/user-lookup.service';
import AiFeedbackBar from '../../components/ai-feedback/ai-feedback-bar';

interface PromptExecutionDetailDrawerProps {
  open: boolean;
  execution: PromptExecutionRecentCall | null;
  onClose: () => void;
}

const Detail = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <p className={`text-sm text-ink ${mono ? 'font-mono break-all' : ''}`}>{value}</p>
  </div>
);

const PromptExecutionDetailDrawer = ({ open, execution, onClose }: PromptExecutionDetailDrawerProps) => {
  // The record stores who ran it as an id; resolve it to a name through the
  // cached lookup and only fall back to the id if nothing resolves it.
  const userId = execution?.userId;
  const { data: userNames } = useQuery({
    queryKey: ['userNames', 'promptExecution', String(userId || '')],
    queryFn: () => userLookupService.getNamesByIds([userId]),
    enabled: !!userId,
    staleTime: 3e5,
  });

  if (!execution) return null;

  const colors = getModelColors(execution.model);
  // Name only — never the raw id. If the lookup cannot resolve it, show a dash
  // rather than an ObjectId a person cannot read.
  const userLabel = (userId && userNames?.get(String(userId))) || '—';

  // The detail record (get-by-id) stores the content under its own field names —
  // `finalPrompt` / `promptOutput` / `systemPrompt` — so read those, falling back
  // to the recent-call's short fields. This is why the sections looked empty:
  // the drawer was reading names the full record does not use.
  const ex = execution as any;
  const promptText: string | undefined = execution.promptText ?? ex.finalPrompt;
  const responseText: string | undefined = execution.responseText ?? ex.promptOutput;
  const systemPrompt: string | undefined = ex.systemPrompt;

  const copy = (text?: string) => { if (text) navigator.clipboard.writeText(text); };

  return (
    <Sidebar
      visible={open}
      onHide={onClose}
      position="right"
      style={{ width: '700px' }}
      header={
        <div className="flex items-center gap-3 px-4 py-3">
          <i className="pi pi-microchip text-purple-600" style={{ fontSize: '1.25rem' }} />
          <span className="text-lg font-semibold text-ink">AI Prompt Details</span>
        </div>
      }
    >
      <div className="flex flex-col h-full p-6" style={{ fontSize: '14px' }}>
        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Model & Status */}
          <div className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-5 mb-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Model</p>
                <span
                  className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}
                >
                  {execution.model}
                </span>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Use Case</p>
                <p className="text-sm font-semibold text-ink">{execution.useCase}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Status</p>
                <Tag value={execution.status} severity={getStatusSeverity(execution.status)} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Timestamp</p>
                <p className="text-sm text-ink font-mono">
                  {formatTimestamp(execution.timestamp)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">Case ID</p>
                <p className="text-sm text-blue-600 dark:text-blue-400 font-mono">{execution.caseId || '-'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wide mb-1">User</p>
                <p className="text-sm text-purple-600 dark:text-purple-400 font-medium break-all">{userLabel}</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 p-4 rounded-lg text-center">
              <p className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-1">Total Tokens</p>
              <p className="text-lg font-bold text-blue-900 dark:text-blue-100">{execution.tokens}</p>
              <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 font-mono">
                {execution.inputTokens}+{execution.outputTokens}
              </p>
            </div>
            <div className="bg-green-50 dark:bg-gray-800 border border-green-200 dark:border-gray-700 p-4 rounded-lg text-center">
              <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide mb-1">Cost</p>
              <p className="text-lg font-bold text-green-900 dark:text-green-100">{formatCurrency(execution.cost)}</p>
            </div>
            <div className="bg-amber-50 dark:bg-gray-800 border border-amber-200 dark:border-gray-700 p-4 rounded-lg text-center">
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400 uppercase tracking-wide mb-1">Response Time</p>
              <p className="text-lg font-bold text-amber-900 dark:text-amber-100">
                {formatResponseTime(execution.responseTime)}
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-gray-800 border border-purple-200 dark:border-gray-700 p-4 rounded-lg text-center">
              <p className="text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wide mb-1">Accuracy</p>
              <p
                className={`text-lg font-bold ${execution.accuracy ? getAccuracyColor(execution.accuracy) : 'text-gray-500'}`}
              >
                {execution.accuracy ? `${execution.accuracy.toFixed(1)}%` : '-'}
              </p>
            </div>
          </div>

          {/* System Prompt */}
          {systemPrompt && (
            <div className="bg-indigo-50 dark:bg-gray-800 border border-indigo-200 dark:border-gray-700 rounded-lg p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className="pi pi-cog text-indigo-600 dark:text-indigo-400" style={{ fontSize: '1rem' }} />
                  <span className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">System Prompt</span>
                </div>
                <button
                  onClick={() => copy(systemPrompt)}
                  className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 text-xs flex items-center gap-1 font-medium"
                >
                  <i className="pi pi-copy" style={{ fontSize: '0.75rem' }} />
                  Copy
                </button>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-indigo-100 dark:border-gray-600 max-h-40 overflow-y-auto">
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{systemPrompt}</p>
              </div>
            </div>
          )}

          {/* Prompt Text */}
          {promptText && (
            <div className="bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 rounded-lg p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className="pi pi-file-edit text-blue-600 dark:text-blue-400" style={{ fontSize: '1rem' }} />
                  <span className="text-sm font-semibold text-blue-800 dark:text-blue-300">Prompt</span>
                </div>
                <button
                  onClick={() => copy(promptText)}
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 text-xs flex items-center gap-1 font-medium"
                >
                  <i className="pi pi-copy" style={{ fontSize: '0.75rem' }} />
                  Copy
                </button>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-blue-100 dark:border-gray-600 max-h-48 overflow-y-auto">
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{promptText}</p>
              </div>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-2 font-mono">
                Prompt Tokens: {execution.inputTokens}
              </p>
            </div>
          )}

          {/* Response Text */}
          {responseText && (
            <div className="bg-teal-50 dark:bg-gray-800 border border-teal-200 dark:border-gray-700 rounded-lg p-5 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className="pi pi-microchip text-teal-600 dark:text-teal-400" style={{ fontSize: '1rem' }} />
                  <span className="text-sm font-semibold text-teal-800 dark:text-teal-300">AI Response</span>
                </div>
                <button
                  onClick={() => copy(responseText)}
                  className="text-teal-600 hover:text-teal-800 dark:text-teal-400 text-xs flex items-center gap-1 font-medium"
                >
                  <i className="pi pi-copy" style={{ fontSize: '0.75rem' }} />
                  Copy
                </button>
              </div>
              <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-teal-100 dark:border-gray-600 max-h-64 overflow-y-auto">
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">{responseText}</p>
              </div>
              <p className="text-xs text-slate-500 dark:text-gray-400 mt-2 font-mono">
                Completion Tokens: {execution.outputTokens}
              </p>

              {/* On the answer itself, not on the drawer: this is the artifact a
                  person can judge, and the execution's own id is what makes the
                  judgement reproducible later. */}
              <AiFeedbackBar
                className="mt-3 border-t border-teal-100 pt-3 dark:border-gray-600"
                componentType="PROMPT_EXECUTION"
                componentCode={ex.promptCode || ex.promptName || undefined}
                componentId={String(ex._id || ex.executionId || '')}
                componentVersion={String(ex.servedModel || ex.modelId || '')}
                runtimeContext={{
                  // What was asked and what came back, so the reviewer judges the
                  // exchange rather than a bare id.
                  userInput: promptText || null,
                  modelResponse: responseText || null,
                  executionId: String(ex._id || ex.executionId || ''),
                  servedModel: ex.servedModel || ex.modelId || null,
                  promptCode: ex.promptCode || null,
                  latencyMs: execution.responseTime ?? null,
                  tokenCount: execution.tokens ?? null,
                  tokensIn: execution.inputTokens ?? null,
                  tokensOut: execution.outputTokens ?? null,
                }}
                traceId={ex.traceId || null}
                chatSessionId={ex.sessionId || ex.chatSessionId || null}
                messageId={ex.messageId || null}
                testId="PromptExecution.Feedback"
              />
            </div>
          )}

          {/* Execution details — the model/runtime facts the top cards don't cover. */}
          <div className="bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg p-5 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <i className="pi pi-server text-slate-600 dark:text-slate-400" style={{ fontSize: '1rem' }} />
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Execution details</span>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              {ex.providerType && (
                <Detail label="Provider" value={String(ex.providerType)} />
              )}
              {(ex.servedModel || ex.modelId) && (
                <Detail label="Served model" value={String(ex.servedModel || ex.modelId)} mono />
              )}
              {ex.cachedTokens != null && (
                <Detail label="Cached tokens" value={String(ex.cachedTokens)} mono />
              )}
              {ex.currency && (
                <Detail label="Currency" value={String(ex.currency)} />
              )}
              {ex.executionDateTime && (
                <Detail label="Executed at" value={formatTimestamp(ex.executionDateTime)} mono />
              )}
              {ex.promptConstructionRecord && (
                <div className="col-span-2">
                  <Detail label="Construction" value={String(ex.promptConstructionRecord)} />
                </div>
              )}
            </div>
          </div>

          {/* Additional Metadata */}
          {(execution as any).metadata && Object.keys((execution as any).metadata).length > 0 && (
            <Accordion className="mb-4">
              <AccordionTab
                header={
                  <div className="flex items-center gap-2">
                    <i className="pi pi-database text-green-600" style={{ fontSize: '1rem' }} />
                    <span className="text-sm font-semibold">Metadata ({Object.keys((execution as any).metadata).length} fields)</span>
                  </div>
                }
              >
                <div className="bg-green-50 dark:bg-gray-700 p-4 rounded-lg">
                  {Object.entries((execution as any).metadata).map(([key, value]) => (
                    <div
                      key={key}
                      className="flex justify-between py-2 border-b border-green-100 dark:border-gray-600 last:border-0 items-center"
                    >
                      <span className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase">{key}:</span>
                      <span className="text-sm text-ink font-mono">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </AccordionTab>
            </Accordion>
          )}

        </div>

        {/* Footer Actions */}
        <div className="border-t border-line pt-5 mt-5 flex justify-end gap-3">
          <Button
            label="Close"
            severity="secondary"
            outlined
            onClick={onClose}
            testId="PromptDetail.Button.Close"
          />
        </div>
      </div>
    </Sidebar>
  );
};

export default PromptExecutionDetailDrawer;
